import React from "react";
import { View, StyleSheet } from "react-native";
import { colors, layout, shadows } from "@/constants";
import { useFeedback } from "@/hooks/feedback";
import { Message } from "@/store/types";
import IconButton from "./base/IconButton";
import Text from "./base/Text";

/**
 * Props for the ChatMessage component
 */
interface ChatMessageProps {
  /** The message data to display */
  message: Message;
}

/**
 * Renders a single chat message bubble with appropriate styling for different roles.
 * User messages appear right-aligned with blue bubbles, agent messages appear left-aligned with white bubbles,
 * and system messages appear centered with gray text and no bubble.
 *
 * User and agent turns show a small "report" action below the bubble (aligned
 * to the message's side, like other chat UIs) that opens the feedback modal
 * seeded with that turn, so feedback can be tied to the exact message.
 *
 * @param props - The component props
 * @returns A styled message bubble
 */
export default function ChatMessage({ message }: ChatMessageProps) {
  const { open } = useFeedback();

  // System messages are ephemeral connection notices — display only, no bubble
  // and no report action.
  if (message.role === "system") {
    return (
      <View style={[styles.container, styles.systemContainer]}>
        <Text size="small" color="tertiary" style={styles.systemText}>
          {message.text}
        </Text>
      </View>
    );
  }

  const isUser = message.role === "user";

  return (
    <View
      style={[styles.container, isUser ? styles.userAlign : styles.agentAlign]}
    >
      <View
        style={[styles.bubble, isUser ? styles.userBubble : styles.agentBubble]}
      >
        <Text color="primary">{message.text}</Text>
      </View>
      <IconButton
        icon="thumbs_up_down"
        size="small"
        color="tertiary"
        onPress={() => open(message)}
        accessibilityLabel="Report this message"
        style={styles.reportButton}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: layout.space.xs
  },
  // Bubble and its action align to the message's side.
  userAlign: {
    alignItems: "flex-end"
  },
  agentAlign: {
    alignItems: "flex-start"
  },
  bubble: {
    maxWidth: "80%",
    paddingHorizontal: layout.space.lg,
    paddingVertical: layout.space.md,
    borderRadius: layout.borderRadius.round,
    ...shadows.subtle
  },
  userBubble: {
    backgroundColor: colors.surfaceContainer,
    borderBottomRightRadius: layout.borderRadius.small
  },
  agentBubble: {
    backgroundColor: colors.tertiaryContainer,
    borderBottomLeftRadius: layout.borderRadius.small
  },
  // Trim the IconButton's vertical padding so the action sits flush under the
  // bubble and doesn't inflate the gap between bubbles.
  reportButton: {
    paddingTop: layout.space.xs
  },
  systemContainer: {
    alignItems: "center",
    marginVertical: layout.space.sm
  },
  systemText: {
    fontStyle: "italic"
  }
});
